import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ImageOptimizationService {
  private readonly RESPONSIVE_BREAKPOINTS = [
    { name: 'xs', width: 320 },
    { name: 'sm', width: 640 },
    { name: 'md', width: 768 },
    { name: 'lg', width: 1024 },
    { name: 'xl', width: 1280 },
    { name: '2xl', width: 1920 }
  ];

  private readonly DEFAULT_QUALITY = 80;
  private webpSupport: boolean | null = null;
  private avifSupport: boolean | null = null;

  constructor() {
    this.detectFormatSupport();
  }

  /**
   * Generate responsive image URLs with different sizes
   */
  getResponsiveImageUrl(baseUrl: string, width: number, quality: number = 80): string {
    if (!baseUrl) {
      return '';
    }
    
    // If using ImageKit.io, add transformation parameters
    if (baseUrl.includes('ik.imagekit.io')) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}tr=w-${width},q-${quality},f-webp`;
    }
    
    // For local images, check if optimized versions exist, otherwise fallback to original
    if (baseUrl.startsWith('/images/') || baseUrl.startsWith('images/')) {
      // For now, return original image until optimized versions are generated
      // This prevents 404 errors while maintaining functionality
      return baseUrl;
      
      // TODO: Uncomment when optimized images are available
      // const pathWithoutExt = baseUrl.replace(/\.[^/.]+$/, '');
      // return `${pathWithoutExt}-${width}w.webp`;
    }
    
    // For other image services or CDNs, return original URL
    return baseUrl;
  }
  
  /**
   * Generate srcset for responsive images
   */
  generateSrcSet(baseUrl: string, sizes: number[] = [320, 640, 1024, 1280]): string {
    if (!baseUrl) {
      return '';
    }
    
    return sizes
      .map(size => `${this.getResponsiveImageUrl(baseUrl, size)} ${size}w`)
      .join(', ');
  }

  /**
   * Generate optimized background image CSS for different screen sizes
   */
  getOptimizedBackgroundImageCss(
    imagePath: string,
    screenSize: 'mobile' | 'tablet' | 'desktop' = 'desktop'
  ): string {
    const breakpoint = this.getBreakpointForScreen(screenSize);
    const webpUrl = this.getResponsiveImageUrl(imagePath, breakpoint.width);
    const fallbackUrl = this.getFallbackImageUrl(imagePath, breakpoint.width);

    return `
      background-image: url('${fallbackUrl}');
      background-image: image-set(
        url('${webpUrl}') type('image/webp'),
        url('${fallbackUrl}') type('image/jpeg')
      );
    `;
  }

  /**
   * Get fallback image URL for browsers that don't support WebP
   */
  getFallbackImageUrl(imagePath: string, width: number): string {
    if (imagePath.includes('ik.imagekit.io')) {
      const separator = imagePath.includes('?') ? '&' : '?';
      return `${imagePath}${separator}tr=w-${width},q-${this.DEFAULT_QUALITY},f-jpg`;
    }
    
    const pathWithoutExt = imagePath.replace(/\.[^/.]+$/, '');
    return `${pathWithoutExt}-${width}w.jpg`;
  }

  /**
   * Detect browser support for modern image formats
   */
  private async detectFormatSupport(): Promise<void> {
    try {
      this.webpSupport = await this.supportsWebP();
      this.avifSupport = await this.supportsAVIF();
    } catch (error) {
      this.webpSupport = false;
      this.avifSupport = false;
    }
  }

  /**
   * Check if browser supports WebP format
   */
  private supportsWebP(): Promise<boolean> {
    return new Promise((resolve) => {
      const webP = new Image();
      webP.onload = webP.onerror = () => {
        resolve(webP.height === 2);
      };
      webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
    });
  }

  /**
   * Check if browser supports AVIF format
   */
  private supportsAVIF(): Promise<boolean> {
    return new Promise((resolve) => {
      const avif = new Image();
      avif.onload = avif.onerror = () => {
        resolve(avif.height === 2);
      };
      avif.src = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgABogQEAwgMg8f8D///8WfhwB8+ErK42A=';
    });
  }

  /**
   * Get the best supported format for the browser
   */
  getBestSupportedFormat(): string {
    if (this.avifSupport) {
      return 'avif';
    } else if (this.webpSupport) {
      return 'webp';
    } else {
      return 'jpeg';
    }
  }

  /**
   * Preload critical images for better performance
   */
  preloadCriticalImages(imagePaths: string[]): Promise<void[]> {
    const preloadPromises = imagePaths.map(path => {
      return new Promise<void>((resolve, reject) => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        // Use original image path for preloading until optimized versions are available
        link.href = path;
        link.onload = () => resolve();
        link.onerror = () => reject(new Error(`Failed to preload: ${path}`));
        document.head.appendChild(link);
      });
    });
    
    return Promise.all(preloadPromises);
  }

  /**
   * Get breakpoint for screen size
   */
  private getBreakpointForScreen(screenSize: 'mobile' | 'tablet' | 'desktop') {
    switch (screenSize) {
      case 'mobile':
        return this.RESPONSIVE_BREAKPOINTS[1]; // 640px
      case 'tablet':
        return this.RESPONSIVE_BREAKPOINTS[3]; // 1024px
      case 'desktop':
      default:
        return this.RESPONSIVE_BREAKPOINTS[5]; // 1920px
    }
  }
  
  /**
   * Get optimized image attributes for different use cases
   */
  getImageAttributes(baseUrl: string, options: {
    width?: number;
    height?: number;
    alt: string;
    loading?: 'eager' | 'lazy';
    fetchpriority?: 'high' | 'low' | 'auto';
    sizes?: string;
  }) {
    const { width = 640, height = 640, alt, loading = 'lazy', fetchpriority = 'auto', sizes } = options;
    
    return {
      src: this.getResponsiveImageUrl(baseUrl, width),
      srcset: this.generateSrcSet(baseUrl),
      width: width.toString(),
      height: height.toString(),
      alt,
      loading,
      fetchpriority,
      sizes: sizes || `(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw`
    };
  }
  
  /**
   * Preload critical images
   */
  preloadImage(url: string): void {
    // Check if already preloaded to avoid duplicates
    const existingLink = document.querySelector(`link[rel="preload"][href="${url}"]`);
    if (existingLink) return;
    
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = url;
    document.head.appendChild(link);
    
    // Remove preload link after it's been used (or after timeout)
    // This prevents the browser warning about unused preloads
    setTimeout(() => {
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
    }, 5000); // Remove after 5 seconds
  }
  
  /**
   * Get placeholder image URL
   */
  getPlaceholderUrl(width: number = 640, height: number = 640, type: 'loading' | 'error' | 'empty' = 'loading'): string {
    const svgContent = this.generatePlaceholderSvg(width, height, type);
    return `data:image/svg+xml;base64,${btoa(svgContent)}`;
  }

  /**
   * Generate different types of placeholder SVGs
   */
  private generatePlaceholderSvg(width: number, height: number, type: 'loading' | 'error' | 'empty'): string {
    const baseRect = `<rect width="100%" height="100%" fill="#f3f4f6"/>`;
    
    switch (type) {
      case 'loading':
        return `
          <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
            ${baseRect}
            <rect x="20%" y="40%" width="60%" height="8%" fill="#e5e7eb" rx="4">
              <animate attributeName="opacity" values="1;0.3;1" dur="2s" repeatCount="indefinite"/>
            </rect>
            <rect x="20%" y="52%" width="40%" height="8%" fill="#e5e7eb" rx="4">
              <animate attributeName="opacity" values="1;0.3;1" dur="2s" begin="0.5s" repeatCount="indefinite"/>
            </rect>
          </svg>
        `;
      
      case 'error':
        return `
          <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
            <rect width="100%" height="100%" fill="#fef2f2"/>
            <circle cx="50%" cy="40%" r="20" fill="none" stroke="#ef4444" stroke-width="2"/>
            <line x1="calc(50% - 8)" y1="calc(40% - 8)" x2="calc(50% + 8)" y2="calc(40% + 8)" stroke="#ef4444" stroke-width="2"/>
            <line x1="calc(50% + 8)" y1="calc(40% - 8)" x2="calc(50% - 8)" y2="calc(40% + 8)" stroke="#ef4444" stroke-width="2"/>
            <text x="50%" y="65%" text-anchor="middle" fill="#dc2626" font-family="system-ui" font-size="14">
              Failed to load
            </text>
          </svg>
        `;
      
      case 'empty':
      default:
        return `
          <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
            ${baseRect}
            <path d="M${width * 0.3} ${height * 0.35}h${width * 0.4}v${height * 0.3}h-${width * 0.4}z" fill="none" stroke="#d1d5db" stroke-width="2"/>
            <circle cx="${width * 0.4}" cy="${height * 0.45}" r="${width * 0.03}" fill="#9ca3af"/>
            <path d="M${width * 0.35} ${height * 0.58}l${width * 0.15} -${height * 0.12}l${width * 0.15} ${height * 0.12}" fill="none" stroke="#d1d5db" stroke-width="2"/>
            <text x="50%" y="75%" text-anchor="middle" fill="#6b7280" font-family="system-ui" font-size="12">
              No image
            </text>
          </svg>
        `;
    }
  }

  /**
   * Generate CSS for gradient placeholder backgrounds
   */
  getGradientPlaceholder(colors: string[] = ['#f3f4f6', '#e5e7eb']): string {
    return `linear-gradient(135deg, ${colors.join(', ')})`;
  }

  /**
   * Get themed placeholder colors for different contexts
   */
  getPlaceholderTheme(theme: 'default' | 'primary' | 'accent' | 'error' = 'default'): { background: string; text: string; border: string } {
    const themes = {
      default: { background: '#f3f4f6', text: '#6b7280', border: '#d1d5db' },
      primary: { background: '#eff6ff', text: '#3b82f6', border: '#93c5fd' },
      accent: { background: '#fef3c7', text: '#d97706', border: '#fbbf24' },
      error: { background: '#fef2f2', text: '#dc2626', border: '#fca5a5' }
    };
    
    return themes[theme];
  }
}
